import { axios } from '@/config/plugins/axios';
import { Route } from 'vue-router/types/router';
import { Next, ProjectUser, UserInfo } from '@/types';
import jwtTokenManager from '@/services/Security/jwtTokenManager';
import userInfoService from '@/services/Security/UserInfo/userInfoService';
import router from '@/config/router/router';
import ErrorHandler from '@/services/errorHandler/ErrorHandler';
import { i18n } from '@/i18n';
import projectInfoService from '@/services/Navigation/ProjectInfoService';
import currentProjectInfoService from '@/services/Navigation/currentProjectInfoService';
import projectCountInfoService from '@/services/Navigation/ProjectCountInfoService';
import notificationManager from '@/services/Notification/NotificationManager';
import { useAppStateStore } from '@/store/modules/appStateStore';

const localLoginCookieName = 'localLoginCookie';
const URL_USER_INFO = '/api/users/me';

export interface User {
	lastname: string;
	gender: string;
	firstname: string;
	mail: string;
	phoneAreaCode: string;
	mobilePhone: string;
	roles: string[];
	projectUsers: ProjectUser[];
	authorizedAuthentication: boolean;
}

const DisabledTwoFactorTokenException = 'App\\Exception\\User\\DisabledTwoFactorTokenException';
const InvalidTermConditionException = 'App\\Exception\\User\\InvalidTermConditionException';
const InvalidCredentialsException = 'App\\Exception\\User\\InvalidCredentialsException';

const loginService = {
	/**
	 * Fonction qui décore une instance axios pour lui ajouter :
	 * - Les entêtes de requêtes qui permettent d'authentifier chaque requête
	 * - Les catch des réponses indiquant que le token n'est plus valide qui vont automatiquement le rafraichir
	 */
	decorateAxiosInstance(jwt: string | null) {
		const authorization = 'Authorization';
		/* eslint-disable @typescript-eslint/no-unused-vars */
		let isUnauthorized = false;

		// We add the authentication token to every header of every request
		axios.defaults.headers.common[authorization] = 'Bearer ' + jwt;
		const loginCallback = () => {
			notificationManager.displayMessage('error', i18n.t('common.security.expired'));

			router.push({ name: 'login' });
		};
		// We look at every response, if the user is not connected, we redirect it to the login page
		ErrorHandler.addGlobalHandlingError('Expired JWT Token', 401, loginCallback);
		// if two factor authent is needed, we redirect to smsvalidation page
		const smsValidationCallback = () => {
			router
				.push({ name: 'smsValidation' })
				.catch(() => isUnauthorized = true);
		};
		ErrorHandler.addGlobalHandlingTypeException(DisabledTwoFactorTokenException, 401, smsValidationCallback);

		// if the user has not accepted the terms of use, he is redirected to the page
		const termConditionValidationCallback = () => {
			router
				.push({ name: 'termConditionValidation' })
				.catch(() => isUnauthorized = true);
		};
		ErrorHandler.addGlobalHandlingTypeException(InvalidTermConditionException, 401, termConditionValidationCallback);

		// if the jwt token is not valid anymore, we disconnect the user
		const logoutCallback = () => {
			loginService.logout();
		};
		ErrorHandler.addGlobalHandlingTypeException(InvalidCredentialsException, 400, logoutCallback);
	},

	/**
	 * Manage the authent for local login
	 * @param to
	 * @param from
	 * @param next
	 */
	checkAuthentication(to: Route, from: Route, next: Next) {
		// If the user isn't logged and don't have a token
		if (!this.isLogged()) {
			// We need to check this after otherwise the previous condition
			// will fail and it will fall to "user already logged in"
			userInfoService.updateAppState(null, true, false);

			// If not in mock mode,
			// log in the user
			if ( to.name !== 'login') {
				next({ name: 'login' });
			}
		} else {
			// we redecorate every time we check user authentication
			// Could be done only on page refresh and user login but we need to find a way to detect refresh
			this.decorateAxiosInstance(jwtTokenManager.getToken(localLoginCookieName));
			userInfoService.updateAppState(userInfoService.getUserInfo(), true, true);

			// If the userinfo cookie is not set, we get the userinfo
			if (!userInfoService.hasUserInfo()) {
				this.getUserInfo();
			}
		}
	},

	/**
	 * Authenticate the user
	 * @param jwtToken
	 * @param jwtEnabled
	 */
	async authenticate(jwtToken: string, jwtEnabled: boolean) {
		jwtTokenManager.saveToken(jwtToken, localLoginCookieName);
		this.decorateAxiosInstance(jwtToken);

		// CallBack after login to save user info
		const saveUserInfoCallBack = () => {
			if (jwtEnabled) {
				router.push({ name: 'projects' });

				return;
			}

			router.push({ name: 'smsValidation' });
		};

		await this.getUserInfo(saveUserInfoCallBack);
	},
	/**
	 * Get the userinfo from the server
	 * @param callback
	 */
	getUserInfo(callback?: () => void) {
		return axios.get(URL_USER_INFO)
			.then((response) => {
				const user = response.data as User;
				const userInfo: UserInfo = {
					lastname: user.lastname,
					firstname: user.firstname,
					// on ne récupère que les noms de services en tant que roles
					roles: user.roles,
					organizationCode: null,
					mail: user.mail,
					phoneAreaCode: user.phoneAreaCode,
					mobilePhone: user.mobilePhone,
					projectUsers: user.projectUsers,
					authorizedAuthentication: user.authorizedAuthentication
				};

				projectCountInfoService.updateProjectCountStore(user.projectUsers.length);

				userInfoService.saveUserInfo(userInfo);
				userInfoService.updateAppState(userInfo, true, true);

				// Check if the callback is a fucntion, so it's optional
				if ('function' === typeof callback && callback) {
					callback();
				}
			})
			.catch((error) => {
				if (error) {
					userInfoService.updateAppState(null, false, false);
					router.push({ name: 'loginerror' });
				}
			});
	},

	/** Check if the user is logged */
	isLogged() {
		return jwtTokenManager.isLoggedIn(localLoginCookieName);
	},

	/**
	 * Déconnecte l'utilisateur courant et supprime le JSON Web Token enregistré en cookie
	 */
	logout() {
		jwtTokenManager.deleteToken(localLoginCookieName);
		userInfoService.updateAppState(null, false, false);
		userInfoService.deleteUserInfo();
		projectInfoService.deleteProjectStores();
		currentProjectInfoService.deleteCurrentProjectStore();
		projectCountInfoService.deleteProjectCountStore();

		router.push({ name: 'login' });
	},

	/**
	 * Envoie une notification lors de l'expiration de la session si l'utilisateur était connecté
	 */
	notifyExpiredSession() {
		const appStateStore = useAppStateStore();

		if (appStateStore.getLoggedAt instanceof Date) {
			// On n'affiche pas le message si ça fait plus de 2h que le token est expiré (1+2)
			const dateToCompare = new Date(appStateStore.getLoggedAt);
			dateToCompare.setHours(dateToCompare.getHours() + 3);

			if (appStateStore.isLogged && dateToCompare > new Date()) {
				notificationManager.displayMessage('error', i18n.t('common.security.expired'));
			}
		}

		userInfoService.updateAppState(null, false, false);
	}
};

export default loginService;
